import React, { useMemo, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import {
  ArrowCircleRight,
  Attractions,
  CampaignRounded,
  LocationOn,
  Newspaper,
  Trophy,
} from '@tmap/mmm-style-guide/src/Icon';
import getPreviewText from '@tmap/mmm-core/slate/getPreviewText';
import { useMediaQuery } from '@tmap/mmm-style-guide/src/useMediaQuery';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { ThemeProvider, useTheme } from '@tmap/mmm-style-guide/src/Theming';
import { topics } from '@tmap/mmm-core/contributionTopics';
import { Chip } from '@tmap/mmm-style-guide/src/Chip';
import CircleIcon from '@tmap/mmm-style-guide/src/CircleIcon';
import { toKey, types } from '@tmap/mmm-core/entityKey';

import { EXTEND_DARK_THEME } from '../../../lib/themes';
import ContributionMedia from '../../communityContent/contributionMedia';
import LinkCard from '../../linkCard';
import useImpressions, { ImpressionTracker } from '../../../hooks/useImpressions';

const StyledLinkCard = styled(LinkCard, {
  shouldForwardProp: (p) => !['hero', 'mini', 'palette'].includes(p),
})(({
  theme,
  hero,
  mini,
  palette,
}) => ({
  background: {
    inverted: theme.palette.grey[900],
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    green: theme.palette.green.dark,
    aqua: theme.palette.aqua.main,
    purple: theme.palette.purple.main,
    favorite: theme.palette.favorite.dark,
    convert: theme.palette.convert.dark,
  }[palette] || theme.palette.grey[900],
  position: 'relative',
  height: !hero && '100%',
  padding: mini && theme.spacing(3),
}));

const CopyWrapper = styled('div', {
  shouldForwardProp: (p) => !['hero', 'mini', 'hasMedia'].includes(p),
})(({
  theme,
  hero,
  mini,
  hasMedia,
}) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  minHeight: '120px',
  paddingLeft: !mini && (!hero || !hasMedia) && theme.spacing(3),
  paddingRight: !mini && theme.spacing(3),
  paddingBottom: !mini && theme.spacing(2),
  paddingTop: hasMedia && !hero ? 0 : theme.spacing(3),
}));

const Header = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
  paddingBottom: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const CircleBackground = styled('div')(({ theme }) => ({
  color: theme.palette.warning.dark,
  background: theme.palette.warning.light,
  padding: theme.spacing(1.5),
  borderRadius: '50px',
  display: 'inline-flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  justifyContent: 'center',
  '& .MuiSvgIcon-root': {
    fontSize: '26px',
  },
}));

const CardTitleTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '20px',
  lineHeight: '35px',
  letterSpacing: '0.15px',
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const CardSubtitleTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '12px',
  lineHeight: '20px',
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
  },
}));

const StyledChip = styled(Chip)(() => ({
  fontSize: '13px',
}));

const DesktopOnly = styled(Grid)(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const MobileOnly = styled(Grid)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
  },
}));

const Spacer = styled('div', {
  shouldForwardProp: (p) => p !== 'show',
})(({ theme, show }) => ({
  marginTop: theme.spacing(show ? 2.5 : 0),
}));

const IconContainer = styled('div')(({ theme }) => ({
  background: '#FFFFFFAA',
  borderRadius: '8px',
  marginTop: theme.spacing(1.5),
  width: '100%',
  padding: theme.spacing(0.75),
}));

const StyledCircleIcon = styled(CircleIcon)(() => ({
  display: 'block',
  fontSize: '24px',
}));

const ProceedIcon = styled(ArrowCircleRight)(({ theme }) => ({
  color: theme.palette.common.white,
}));

function LocationLabel(props) {
  const {
    location,
    palette,
  } = props;
  return (
    <IconContainer>
      <Grid container spacing={1} alignItems='center' justifyContent='center'>
        <Grid item>
          <StyledCircleIcon
            Icon={LocationOn}
            color={palette}
            variant='contained'
            shrinkFactor='1 / 2'
          />
        </Grid>
        <Grid item xs>
          <Typography variant='body2' fontSize='12px' fontWeight={500} color='textSecondary'>{location.description}</Typography>
        </Grid>
      </Grid>
    </IconContainer>
  );
}

export const iconsLibrary = [
  { value: 'none', label: 'None' },
  { value: 'trophy', label: 'Trophy', icon: <Trophy /> },
  { value: 'megaphone', label: 'Megaphone', icon: <CampaignRounded /> },
  { value: 'attractions', label: 'Attractions', icon: <Attractions /> },
  { value: 'newspaper', label: 'Newspaper', icon: <Newspaper /> },
];
const iconsMap = Object.fromEntries(iconsLibrary.map(({ value, icon }) => [value, icon]));

function MiniMedia(props) {
  const { media } = props;
  const ratio = media.width / media.height;
  if (ratio > 0.95 && ratio < 1.05) {
    return (
      <div style={{ height: '116px', width: '116px' }}>
        <ContributionMedia
          media={media}
          width={232}
          height={232}
          borderRadius={8}
          fitHeight
        />
      </div>
    );
  }
  if (media.width > media.height) {
    return (
      <div style={{ height: '116px', width: '260px', maxWidth: '100%' }}>
        <ContributionMedia
          media={media}
          width={512}
          height={232}
          borderRadius={8}
          fitHeight
        />
      </div>
    );
  }
  return (
    <div style={{ height: '116px', width: '116px' }}>
      <ContributionMedia
        media={media}
        width={232}
        height={232}
        borderRadius={400}
        fitHeight
      />
    </div>
  );
}

function BasicMedia(props) {
  const { media } = props;
  return (
    <div style={{ height: '152px' }}>
      <ContributionMedia
        media={media}
        width={500}
        height={300}
        borderRadius={0}
        fitHeight
      />
    </div>
  );
}

function HeroMedia(props) {
  const { media } = props;
  return (
    <div style={{ height: '415px' }}>
      <ContributionMedia
        media={media}
        width={512}
        height={415}
        borderRadius={0}
        fitHeight
      />
    </div>
  );
}

function FeatureCard(props) {
  const {
    item,
    placementId,
    hero = false,
    mini = false,
    palette = 'dark',
  } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const contribution = item?.contribution || { showBlank: true };
  const {
    topics: contributionTopics = [],
    hashtags = [],
  } = contribution;
  const previewText = getPreviewText(
    contribution.content,
    { maxLength: mini ? 100 : 140, ellipse: true },
  );
  const chipValues = useMemo(() => {
    const allChips = [];
    contributionTopics.forEach((topicId) => {
      const topic = topics[topicId]?.plural;
      if (topic) {
        allChips.push({
          id: topicId,
          label: topic,
        });
      }
    });
    allChips.push(...hashtags.map((hashtag) => ({
      id: hashtag,
      label: hashtag,
    })));
    return allChips;
  }, [contributionTopics, hashtags]);

  const { trackView, trackClick } = useImpressions(
    toKey(types.CONTRIBUTION, contribution._id),
    placementId,
  );

  const finalPalette = contribution?.media?.[0]?.themePalette?.[0] || palette;
  if (contribution.showBlank) return null;
  const hasMedia = contribution?.media?.length > 0;
  return (
    <ImpressionTracker trackView={trackView} trackClick={trackClick}>
      <StyledLinkCard href={`/c/${contribution.slug}`} variant='contained' palette={finalPalette} hero={hero} mini={mini}>
        <Grid container spacing={3} height='calc(100% + 24px)'>
          {contribution?.media?.[0] && (
            mini ? (
              <Grid item xs={12} md='auto'>
                <MiniMedia media={contribution.media[0]} />
              </Grid>
            ) : (
              <Grid item xs={12} sm={hero ? 6 : 12}>
                {(isMobile || !hero) ? (
                  <BasicMedia media={contribution.media[0]} />
                ) : (
                  <HeroMedia media={contribution.media[0]} />
                )}
              </Grid>
            )
          )}
          <Grid item xs>
            <CopyWrapper hero={hero} mini={mini} hasMedia={hasMedia}>
              <ThemeProvider theme={EXTEND_DARK_THEME}>
                <Grid container spacing={0.5}>
                  {chipValues.map(({ id, label }) => (
                    <Grid item key={`topic-chip-${id}`}>
                      <StyledChip
                        variant='filled'
                        size='small'
                        label={label}
                      />
                    </Grid>
                  ))}
                </Grid>
                <CardTitleTypography marginTop={mini ? 1 : 3}>
                  {
                    contribution.title || getPreviewText(
                      contribution.content,
                      { maxLength: mini ? 60 : 120, ellipse: true },
                    )
                  }
                </CardTitleTypography>
                {contribution.title && previewText && (!mini || !hasMedia) && (
                  <CardSubtitleTypography variant='subtitle2' component='div'>
                    {previewText}
                  </CardSubtitleTypography>
                )}
              </ThemeProvider>
              {contribution.location ? (
                <Box marginTop='auto'>
                  <LocationLabel location={contribution.location} palette={finalPalette} />
                </Box>
              ) : (
                <Box textAlign='right' marginTop='auto'>
                  <ProceedIcon />
                </Box>
              )}
            </CopyWrapper>
          </Grid>
        </Grid>
      </StyledLinkCard>
    </ImpressionTracker>
  );
}

function FeatureVibrant(props) {
  const {
    heading,
    content = [],
    icon,
    blockIndex,
    isPreview = false,
  } = props;
  const heroIcon = iconsMap[icon];
  const layoutDesktop = ([
    [12],
    [6, 6],
    [6, 6, 12],
    [6, 6, 6, 6],
    [6, 6, 12, 6, 6],
  ])[content.length - 1] || new Array(content.length).fill(6);
  const mobilePrimaryContent = [...content];
  const mobileSecondaryContent = [];
  if (mobilePrimaryContent.length > 3) {
    const items = mobilePrimaryContent.splice(mobilePrimaryContent.length - 2, 2);
    mobileSecondaryContent.push(...items);
  }
  const paletteOptions = ['inverted', 'primary', 'purple', 'favorite', 'convert'];
  const swiperRef = useRef();
  return (
    <>
      {(heading || heroIcon) && (
        <Header>
          {heroIcon && (
            <CircleBackground>
              {heroIcon}
            </CircleBackground>
          )}
          {heading && (
            <Typography fontWeight={800} fontSize='24px' lineHeight='32px' textAlign='center'>
              {heading}
            </Typography>
          )}
        </Header>
      )}
      {content.length > 0 ? (
        <Spacer show={heading || heroIcon}>
          <DesktopOnly container spacing={2.5}>
            {content.map(((item, i) => (
              <Grid item xs={layoutDesktop[i]} key={i}>
                <FeatureCard
                  item={item}
                  placementId={!isPreview && `contentBlock-${blockIndex}-featureVibrant-${i}`}
                  hero={i === 0 && content.length === 1}
                  mini={i >= 2}
                  palette={paletteOptions[i]}
                />
              </Grid>
            )))}
          </DesktopOnly>
          <MobileOnly container spacing={2.5}>
            {mobilePrimaryContent.map(((item, i) => (
              <Grid item xs={12} key={i}>
                <FeatureCard
                  item={item}
                  placementId={!isPreview && `contentBlock-${blockIndex}-featureVibrant-${i}`}
                  palette={paletteOptions[i]}
                />
              </Grid>
            )))}
            {mobileSecondaryContent.length > 0 && (
              <Grid item xs={12}>
                <Swiper
                  ref={swiperRef}
                  slidesPerView='auto'
                  spaceBetween={20}
                  modules={[Navigation]}
                >
                  {mobileSecondaryContent.map((item, i) => (
                    <SwiperSlide key={`slide-${i}`} style={{ maxWidth: 'max(33.33vw, 300px)', height: 'auto' }}>
                      <FeatureCard
                        item={item}
                        placementId={!isPreview && `contentBlock-${blockIndex}-featureVibrant-${mobilePrimaryContent.length + i}`}
                        mini
                        palette={paletteOptions[mobilePrimaryContent.length + i]}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </Grid>
            )}
          </MobileOnly>
        </Spacer>
      ) : null}
    </>
  );
}

export default FeatureVibrant;
